import React, { useState } from 'react';
import type { AppLanguage, SummaryLanguage } from '../../global';
import type { ToastKind } from '../../components/Toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { Label, Separator } from '../../components/ui/Input';
import { Select, type SelectOption } from '../../components/ui/Select';
import { useLanguage } from '../../lib/i18n';
import { usePreferences } from './usePreferences';

interface LanguageSectionProps {
  onNotify: (kind: ToastKind, text: string) => void;
}

/** Solda etiket + açıklama, sağda kontrol — Genel bölümüyle aynı satır düzeni. */
const SettingRow: React.FC<{
  label: string;
  hint: string;
  labelId: string;
  hintId: string;
  children: React.ReactNode;
}> = ({ label, hint, labelId, hintId, children }) => (
  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
    <div className="space-y-1.5">
      <Label id={labelId}>{label}</Label>
      <p id={hintId} className="text-[0.8rem] text-muted-foreground">
        {hint}
      </p>
    </div>
    {children}
  </div>
);

/**
 * Ayarlar > Dil: uygulamanın arayüz dili ve yapay zekâ özetlerinin dili.
 * İkisi ayrı ayarlar — Türkçe arayüzde İngilizce özet istenebiliyor.
 */
export const LanguageSection: React.FC<LanguageSectionProps> = ({ onNotify }) => {
  const { language, setLanguage, t } = useLanguage();
  const { prefs, savePrefs } = usePreferences();
  const [isSavingLanguage, setIsSavingLanguage] = useState(false);
  const [isSavingSummary, setIsSavingSummary] = useState(false);

  // Her dil kendi adıyla yazılıyor; altında seçili arayüz dilindeki adı var.
  const languageOptions: SelectOption<AppLanguage>[] = [
    { value: 'tr', label: 'Türkçe', hint: t('', 'Turkish') || undefined },
    { value: 'en', label: 'English', hint: t('İngilizce', '') || undefined },
  ];

  const summaryOptions: SelectOption<SummaryLanguage>[] = [
    {
      value: 'auto',
      label: t('Otomatik', 'Automatic'),
      hint: t('Konuşmanın dili', 'The language spoken'),
    },
    { value: 'tr', label: 'Türkçe' },
    { value: 'en', label: 'English' },
  ];

  const changeLanguage = async (next: AppLanguage) => {
    setIsSavingLanguage(true);
    try {
      await setLanguage(next);
    } catch {
      onNotify('error', t('Dil değiştirilemedi. Tekrar deneyin.', 'Could not change the language. Please try again.'));
    } finally {
      setIsSavingLanguage(false);
    }
  };

  const changeSummaryLanguage = async (next: SummaryLanguage) => {
    setIsSavingSummary(true);
    try {
      await savePrefs({ summaryLanguage: next });
    } catch {
      onNotify('error', t('Özet dili kaydedilemedi.', 'Could not save the summary language.'));
    } finally {
      setIsSavingSummary(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('Dil', 'Language')}</CardTitle>
        <CardDescription>
          {t(
            'Arayüzün dili ile toplantı özetlerinin dili ayrı ayrı seçilebilir.',
            'The language of the app and the language of your meeting summaries are set separately.'
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <SettingRow
          label={t('Uygulama dili', 'App language')}
          hint={t(
            'Menüler, düğmeler ve bildirimler bu dilde gösterilir.',
            'Menus, buttons and notifications are shown in this language.'
          )}
          labelId="app-language-label"
          hintId="app-language-hint"
        >
          <Select
            id="app-language"
            aria-labelledby="app-language-label"
            aria-describedby="app-language-hint"
            value={language}
            options={languageOptions}
            onChange={(next) => void changeLanguage(next)}
            disabled={isSavingLanguage}
            className="w-full shrink-0 sm:w-44"
          />
        </SettingRow>

        <Separator />

        <SettingRow
          label={t('Özet dili', 'Summary language')}
          hint={t(
            'Toplantı özetleri, başlıkları ve kararlar bu dilde yazılır. Otomatik seçiliyken konuşma hangi dildeyse özet de o dilde olur.',
            'Meeting summaries, titles and decisions are written in this language. With Automatic, the summary follows the language spoken in the meeting.'
          )}
          labelId="summary-language-label"
          hintId="summary-language-hint"
        >
          <Select
            id="summary-language"
            aria-labelledby="summary-language-label"
            aria-describedby="summary-language-hint"
            value={prefs.summaryLanguage}
            options={summaryOptions}
            onChange={(next) => void changeSummaryLanguage(next)}
            disabled={isSavingSummary}
            className="w-full shrink-0 sm:w-44"
          />
        </SettingRow>
      </CardContent>
    </Card>
  );
};
